import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Loader, X, ArrowRight, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import ColumnMapper from './ColumnMapper';
import ImportPreview from './ImportPreview';
import ExcelImportHelpModal from './ExcelImportHelpModal';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';

const ExcelImport = ({ entityName, onClose, onImportComplete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Import Complete
  const [file, setFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [entityFields, setEntityFields] = useState([]);
  const [rowRange, setRowRange] = useState({ loadAll: true, startRow: 2, endRow: null }); // startRow is 1-based, row 1 is header
  const [upsertKeys, setUpsertKeys] = useState([]); // Fields to use for upsert matching
  const [mappings, setMappings] = useState({});
  const [autoMappings, setAutoMappings] = useState({});
  const [validation, setValidation] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const [skipErrorRows, setSkipErrorRows] = useState(false);
  const [customTags, setCustomTags] = useState(''); // User-provided tags
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    upsertKeys: false,
    importTags: false,
    rowRange: false
  });

  // Get API headers with tenant code
  const getApiHeaders = (contentType = null) => {
    const tenantCode = getProtectedTenantCode();
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
    };
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return headers;
  };

  // Load entity fields on mount
  useEffect(() => {
    loadEntityFields();
  }, [entityName]);

  const loadEntityFields = async () => {
    try {
      const response = await axios.get(`/api/excel/import/${entityName}/fields`, {
        headers: getApiHeaders()
      });
      if (response.data.success) {
        setEntityFields(response.data.fields);
      }
    } catch (error) {
      console.error('Failed to load entity fields:', error);
      toast.error(t('excelImport.loadFieldsError', 'Failed to load entity fields'));
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.toLowerCase().split('.').pop();
      if (!['xlsx', 'xls'].includes(ext)) {
        toast.error(t('excelImport.invalidFileType', 'Please select an Excel file (.xlsx or .xls)'));
        return;
      }
      setFile(selectedFile);
      setError(null);
      // Automatically upload the file
      uploadFile(selectedFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      const ext = droppedFile.name.toLowerCase().split('.').pop();
      if (!['xlsx', 'xls'].includes(ext)) {
        toast.error(t('excelImport.invalidFileType', 'Please select an Excel file (.xlsx or .xls)'));
        return;
      }
      setFile(droppedFile);
      setError(null);
      // Automatically upload the file
      uploadFile(droppedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload) {
      toast.error(t('excelImport.selectFile', 'Please select a file first'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      if (selectedSheet) {
        formData.append('sheetName', selectedSheet);
      }
      if (!rowRange.loadAll) {
        if (rowRange.startRow) formData.append('startRow', rowRange.startRow);
        if (rowRange.endRow) formData.append('endRow', rowRange.endRow);
      }

      const response = await axios.post(
        `/api/excel/import/${entityName}/upload`,
        formData,
        {
          headers: getApiHeaders('multipart/form-data')
        }
      );

      if (response.data.success) {
        setUploadedFile(response.data.file);
        setExcelData(response.data.data);
        setSheetNames(response.data.sheets || []);
        setSelectedSheet(response.data.data.sheetName);
        setAutoMappings(response.data.autoMappings || {});
        
        // Initialize mappings with auto-detected ones
        const initialMappings = {};
        Object.entries(response.data.autoMappings || {}).forEach(([excelColumn, mapping]) => {
          initialMappings[excelColumn] = {
            fieldName: mapping.fieldName,
            displayName: mapping.displayName
          };
        });
        setMappings(initialMappings);
        
        setStep(2);
        toast.success(t('excelImport.uploadSuccess', 'File uploaded successfully'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || t('excelImport.uploadError', 'Failed to upload file');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (excelColumn, fieldName, multiFieldMapping = null) => {
    const newMappings = {
      ...mappings,
      [excelColumn]: multiFieldMapping || (fieldName ? {
        fieldName,
        displayName: entityFields.find(f => f.fieldName === fieldName)?.displayName || fieldName
      } : null)
    };
    setMappings(newMappings);
  };

  const handleSheetChange = async (newSheetName) => {
    if (!uploadedFile || !newSheetName || newSheetName === selectedSheet) return;
    
    setLoading(true);
    setError(null);

    try {
      // Re-parse the file with the new sheet name
      const formData = new FormData();
      formData.append('file', file); // Re-use the original file
      formData.append('sheetName', newSheetName);
      if (!rowRange.loadAll) {
        if (rowRange.startRow) formData.append('startRow', rowRange.startRow);
        if (rowRange.endRow) formData.append('endRow', rowRange.endRow);
      }

      const response = await axios.post(
        `/api/excel/import/${entityName}/upload`,
        formData,
        {
          headers: getApiHeaders('multipart/form-data')
        }
      );

      if (response.data.success) {
        setSelectedSheet(newSheetName);
        setExcelData(response.data.data);
        setAutoMappings(response.data.autoMappings || {});
        
        // Re-initialize mappings with auto-detected ones
        const initialMappings = {};
        Object.entries(response.data.autoMappings || {}).forEach(([excelColumn, mapping]) => {
          initialMappings[excelColumn] = {
            fieldName: mapping.fieldName,
            displayName: mapping.displayName
          };
        });
        setMappings(initialMappings);
        toast.success(t('excelImport.sheetChanged', 'Sheet changed successfully'));
      }
    } catch (error) {
      console.error('Sheet change error:', error);
      const errorMessage = error.response?.data?.message || error.message || t('excelImport.sheetChangeError', 'Failed to change sheet');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!uploadedFile || Object.keys(mappings).filter(k => mappings[k]?.fieldName).length === 0) {
      toast.error(t('excelImport.mapColumnsFirst', 'Please map at least one column first'));
      return;
    }

    setLoading(true);
    setError(null);
    setSkipErrorRows(false); // Reset skip option when re-validating

    try {
      const requestBody = {
        filePath: uploadedFile.path,
        mappings: mappings,
        sheetName: selectedSheet,
        upsertKeys: entityName === 'customer' ? upsertKeys : [] // Pass upsert keys for customer entity
      };
      
      if (!rowRange.loadAll) {
        if (rowRange.startRow) requestBody.startRow = rowRange.startRow;
        if (rowRange.endRow) requestBody.endRow = rowRange.endRow;
      }

      const response = await axios.post(
        `/api/excel/import/${entityName}/validate`,
        requestBody,
        {
          headers: getApiHeaders('application/json')
        }
      );

      if (response.data.success) {
        setValidation(response.data.validation);
        setPreviewData(response.data.preview || []);
        setStep(3);
        toast.success(t('excelImport.validationComplete', 'Validation complete'));
      }
    } catch (error) {
      console.error('Validation error:', error);
      const errorMessage = error.response?.data?.message || error.message || t('excelImport.validationError', 'Validation failed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error(t('excelImport.noFile', 'No file to import'));
      return;
    }

    setImporting(true);
    setError(null);

    try {
      // Direct import endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      if (selectedSheet) {
        formData.append('sheetName', selectedSheet);
      }
      if (!rowRange.loadAll) {
        if (rowRange.startRow) formData.append('startRow', rowRange.startRow);
        if (rowRange.endRow) formData.append('endRow', rowRange.endRow);
      }
      if (entityName === 'customer' && upsertKeys.length > 0) {
        formData.append('upsertKeys', JSON.stringify(upsertKeys));
      }
      // Add tags
      formData.append('customTags', customTags);
      formData.append('skipErrorRows', skipErrorRows);

      const response = await axios.post(
        `/api/excel/import/customer/direct`,
        formData,
        {
          headers: getApiHeaders('multipart/form-data')
        }
      );

      if (response.data.success) {
        setImportResults(response.data.results);
        toast.success(
          t('excelImport.importComplete', 'Import completed successfully!')
        );
        
        // Call onImportComplete callback if provided
        if (onImportComplete) {
          onImportComplete(response.data.results);
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('excelImport.importError', 'Failed to import data');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };


  const handleReset = () => {
    setFile(null);
    setUploadedFile(null);
    setExcelData(null);
    setMappings({});
    setAutoMappings({});
    setValidation(null);
    setPreviewData([]);
    setImportResults(null);
    setError(null);
    setSkipErrorRows(false);
    setCustomTags('');
    setExpandedSections({ upsertKeys: false, importTags: false, rowRange: false });
    setStep(1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <FileSpreadsheet className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('excelImport.title', `Import ${entityName.charAt(0).toUpperCase() + entityName.slice(1)} from Excel`)}
            </h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowHelpModal(true)}
              className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="ดูคู่มือการใช้งาน"
            >
              <HelpCircle className="h-6 w-6" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((stepNum) => (
              <React.Fragment key={stepNum}>
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      step >= stepNum
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {step > stepNum ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <span>{stepNum}</span>
                    )}
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {stepNum === 1 && t('excelImport.upload', 'Upload')}
                    {stepNum === 2 && t('excelImport.map', 'Map')}
                    {stepNum === 3 && t('excelImport.complete', 'Complete')}
                  </span>
                </div>
                {stepNum < 3 && (
                  <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700 mx-4">
                    <div
                      className={`h-full ${
                        step > stepNum ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                      style={{ width: step > stepNum ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 rounded-lg border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-red-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold mb-2 text-red-700 dark:text-red-300">
                    Error
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center hover:border-blue-500 transition-colors"
              >
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('excelImport.dropFile', 'Drop your Excel file here')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {t('excelImport.orClick', 'or click to browse')}
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer"
                >
                  {t('excelImport.selectFile', 'Select File')}
                </label>
                {file && (
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left Column - Sheet Selection and Column Mapper */}
              <div className="lg:col-span-2 space-y-4">
                {/* Sheet Selection */}
                {sheetNames && sheetNames.length > 1 && (
                  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('excelImport.selectSheet', 'Select Sheet')}
                    </label>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                      {sheetNames.map((sheet) => (
                        <button
                          key={sheet}
                          type="button"
                          onClick={() => handleSheetChange(sheet)}
                          disabled={loading}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            selectedSheet === sheet
                              ? 'bg-blue-500 text-white dark:bg-blue-600'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {sheet}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {t('excelImport.sheetInfo', 'Currently viewing: {sheet}', { sheet: excelData?.sheetName })}
                    </p>
                  </div>
                )}
                
                <ColumnMapper
                  excelHeaders={excelData?.headers || []}
                  entityFields={entityFields}
                  mappings={mappings}
                  onMappingChange={handleMappingChange}
                  autoMappings={autoMappings}
                  sampleData={excelData?.previewRows?.[0] || null}
                />
              </div>

              {/* Right Column - Collapsible Options */}
              <div className="space-y-4">
                {/* Upsert Key Selection - Allow users to specify which fields to use for matching existing records */}
                {entityName === 'customer' && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, upsertKeys: !prev.upsertKeys }))}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        {t('excelImport.upsertKeys', 'Upsert Keys (Optional)')}
                      </label>
                      {expandedSections.upsertKeys ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.upsertKeys && (
                      <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 mt-3">
                          {t('excelImport.upsertKeysInfo', 'Select fields to match existing records. If a record matches, it will be updated; otherwise, a new record will be created.')}
                        </p>
                        <div className="space-y-2">
                          {[
                            { key: 'CustomerCode', label: t('excelImport.upsertKeyCustomerCode', 'Customer Code') },
                            { key: 'Email', label: t('excelImport.upsertKeyEmail', 'Email') },
                            { key: 'FirstName+LastName', label: t('excelImport.upsertKeyName', 'First Name + Last Name') },
                            { key: 'CompanyName', label: t('excelImport.upsertKeyCompanyName', 'Company Name') },
                            { key: 'Phone', label: t('excelImport.upsertKeyPhone', 'Phone') }
                          ].map((option) => (
                            <label key={option.key} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={upsertKeys.includes(option.key)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setUpsertKeys([...upsertKeys, option.key]);
                                  } else {
                                    setUpsertKeys(upsertKeys.filter(k => k !== option.key));
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Import Tags Section */}
                {entityName === 'customer' && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, importTags: !prev.importTags }))}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        📌 {t('excelImport.importTags', 'Import Tags')}
                      </label>
                      {expandedSections.importTags ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.importTags && (
                      <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mt-3">
                          <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            {t('excelImport.sourceInfo', 'Source Information')}
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-400">
                            {t('excelImport.sourceInfoText', 'The source column will be automatically set to "Excel - {datetime}" to identify the import source and timestamp', { datetime: new Date().toISOString().split('.')[0] + 'Z' })}
                          </p>
                        </div>
                        
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                          {t('excelImport.customTags', 'Custom Tags (comma-separated)')}
                        </label>
                        <input
                          type="text"
                          value={customTags}
                          onChange={(e) => setCustomTags(e.target.value)}
                          placeholder={t('excelImport.customTagsPlaceholder', 'e.g., Q4-2025, Trade Show, VIP Customers')}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                        />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          {t('excelImport.customTagsInfo', 'Add optional custom tags to help organize and filter imported records')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Row Range Selection */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, rowRange: !prev.rowRange }))}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      {t('excelImport.rowRange', 'Row Range Selection')}
                    </label>
                    {expandedSections.rowRange ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections.rowRange && (
                    <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="space-y-3 mt-3">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="loadAll"
                            name="rowRange"
                            checked={rowRange.loadAll}
                            onChange={() => setRowRange({ loadAll: true, startRow: 2, endRow: null })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="loadAll" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            {t('excelImport.loadAllRows', 'Load All Rows')}
                          </label>
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="loadRange"
                            name="rowRange"
                            checked={!rowRange.loadAll}
                            onChange={() => setRowRange({ loadAll: false, startRow: 2, endRow: excelData?.totalRows ? excelData.totalRows + 1 : null })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="loadRange" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            {t('excelImport.loadRange', 'Load Specific Range')}
                          </label>
                        </div>
                        
                        {!rowRange.loadAll && (
                          <div className="ml-6 flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                {t('excelImport.startRow', 'Start Row')} (Row 1 is header)
                              </label>
                              <input
                                type="number"
                                min="2"
                                value={rowRange.startRow || ''}
                                onChange={(e) => setRowRange(prev => ({ ...prev, startRow: parseInt(e.target.value) || 2 }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                                placeholder="2"
                              />
                            </div>
                            <div className="flex items-center pt-6">
                              <span className="text-gray-500 dark:text-gray-400">-</span>
                            </div>
                            <div className="flex-1">
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                {t('excelImport.endRow', 'End Row')} (Leave empty for all)
                              </label>
                              <input
                                type="number"
                                min="2"
                                value={rowRange.endRow || ''}
                                onChange={(e) => setRowRange(prev => ({ ...prev, endRow: e.target.value ? parseInt(e.target.value) : null }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                                placeholder={t('excelImport.allRows', 'All')}
                              />
                            </div>
                          </div>
                        )}
                        
                        {excelData && (
                          <p className="ml-6 text-xs text-gray-500 dark:text-gray-400">
                            {t('excelImport.totalRowsInfo', `Total rows in sheet: ${excelData.totalRows}`)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && !importResults && (
            <div className="space-y-4">
              <ImportPreview
                previewData={previewData}
                validation={validation}
                totalRows={excelData?.totalRows || 0}
              />
              
              {/* Skip Error Rows Option - Show only if there are errors */}
              {validation && !validation.valid && validation.rowErrors && Object.keys(validation.rowErrors).length > 0 && (() => {
                const errorRowCount = Object.keys(validation.rowErrors).length;
                // Use previewData length as the total, as it represents actual rows being imported
                const totalRows = previewData?.length || excelData?.totalRows || 0;
                const validRowCount = totalRows - errorRowCount;
                
                return (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                          {t('excelImport.validationErrors', 'Validation Errors Found')}
                        </h4>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                          {t('excelImport.errorRowsFound', `${errorRowCount} row(s) have validation errors. You can skip these rows and import only the valid records.`)}
                        </p>
                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white dark:bg-gray-800 rounded-lg border-2 border-yellow-300 dark:border-yellow-600 hover:border-yellow-400 dark:hover:border-yellow-500 transition-colors">
                          <input
                            type="checkbox"
                            checked={skipErrorRows}
                            onChange={(e) => setSkipErrorRows(e.target.checked)}
                            className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                          />
                          <span className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">
                            {t('excelImport.skipErrorRows', `✓ Skip error rows and continue with ${validRowCount} valid record(s)`)}
                          </span>
                        </label>
                        
                        {!skipErrorRows && (
                          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded flex items-start">
                            <AlertCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-700 dark:text-red-300">
                              {t('excelImport.checkboxRequired', 'Check the box above to enable the "Continue" button and proceed with valid rows only.')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {step === 3 && importResults && (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                    {t('excelImport.importComplete', 'Import Complete')}
                  </h3>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                  {t('excelImport.importCompleteInfo', 'Your data has been successfully imported to the database.')}
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('excelImport.successfulRecords', 'Successful Records')}
                    </p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {importResults.successCount || 0}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('excelImport.totalRows', 'Total Rows')}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {importResults.totalRows || 0}
                    </p>
                  </div>
                </div>

                {/* Dual Import Statistics */}
                {(importResults.dualImportCount > 0 || importResults.personOnlyCount > 0 || importResults.companyOnlyCount > 0) && (
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {importResults.personOnlyCount > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {t('excelImport.personOnly', 'Person Only')}
                        </p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {importResults.personOnlyCount}
                        </p>
                      </div>
                    )}
                    {importResults.companyOnlyCount > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {t('excelImport.companyOnly', 'Company Only')}
                        </p>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {importResults.companyOnlyCount}
                        </p>
                      </div>
                    )}
                    {importResults.dualImportCount > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {t('excelImport.dualRecords', 'Dual (Person+Company)')}
                        </p>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {importResults.dualImportCount}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {importResults.errorCount > 0 && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      {t('excelImport.errorCount', `${importResults.errorCount} row(s) had errors and were skipped`)}
                    </p>
                  </div>
                )}

                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      {t('excelImport.errors', 'Errors')}
                    </h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {importResults.errors.slice(0, 10).map((error, idx) => (
                        <div
                          key={idx}
                          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 text-sm"
                        >
                          <p className="font-medium text-red-800 dark:text-red-200">
                            {t('excelImport.row', 'Row')} {error.row}:
                          </p>
                          <ul className="list-disc list-inside text-red-700 dark:text-red-300">
                            {Array.isArray(error.errors) ? error.errors.map((err, errIdx) => (
                              <li key={errIdx}>{err}</li>
                            )) : <li>{error.errors || error.message}</li>}
                          </ul>
                        </div>
                      ))}
                      {importResults.errors.length > 10 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                          {t('excelImport.moreErrors', `... and ${importResults.errors.length - 10} more errors`)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={() => {
              // Allow going back on all steps except step 1 and after import is complete
              if (step > 1 && !importResults) {
                setStep(step - 1);
              } else {
                onClose();
              }
            }}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || importing}
          >
            {step > 1 && !importResults 
              ? t('common.back', 'Back') 
              : importResults 
                ? t('common.close', 'Close')
                : t('common.cancel', 'Cancel')
            }
          </button>
          <div className="flex space-x-3">
            {step === 1 && loading && (
              <button
                disabled={true}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                {t('excelImport.uploading', 'Uploading...')}
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleValidate}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    {t('excelImport.validating', 'Validating...')}
                  </>
                ) : (
                  <>
                    {t('excelImport.validate', 'Validate & Preview')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </button>
            )}
            {step === 3 && !importResults && (() => {
              const errorRowCount = validation?.rowErrors ? Object.keys(validation.rowErrors).length : 0;
              // Use previewData length as the total, as it represents actual rows being imported
              const totalRows = previewData?.length || excelData?.totalRows || 0;
              const validRowCount = totalRows - errorRowCount;
              const hasErrors = validation && !validation.valid && errorRowCount > 0;
              
              return (
                <>
                  <button
                    onClick={handleImport}
                    disabled={importing || (hasErrors && !skipErrorRows) || validRowCount === 0}
                    className={`px-6 py-2.5 rounded-lg font-semibold flex items-center transition-all ${
                      importing || (hasErrors && !skipErrorRows) || validRowCount === 0
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-green-500 text-white hover:bg-green-600 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {importing ? (
                      <>
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        {t('excelImport.importing', 'Importing...')}
                      </>
                    ) : (
                      <>
                        {hasErrors && !skipErrorRows ? (
                          <>
                            <X className="h-5 w-5 mr-2" />
                            {t('excelImport.cannotContinue', 'Cannot Continue (Check box to skip errors)')}
                          </>
                        ) : skipErrorRows && hasErrors ? (
                          <>
                            <CheckCircle className="h-5 w-5 mr-2" />
                            {t('excelImport.importValidRowsOnly', `Import ${validRowCount} Valid Rows`)}
                            <ArrowRight className="h-5 w-5 ml-2" />
                          </>
                        ) : (
                          <>
                            {t('excelImport.importNow', 'Import Now')}
                            <ArrowRight className="h-5 w-5 ml-2" />
                          </>
                        )}
                      </>
                    )}
                  </button>
                  
                  {/* Helper text below button when disabled due to errors */}
                  {!importing && hasErrors && !skipErrorRows && validRowCount > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400 italic">
                      ⚠️ {t('excelImport.checkboxToEnable', 'Check the "Skip error rows" box above to enable this button')}
                    </p>
                  )}
                </>
              );
            })()}
            {step === 3 && importResults && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {t('common.close', 'Close')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Help Modal */}
      <ExcelImportHelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
      />
    </div>
  );
};

export default ExcelImport;

